import { Box, CircularProgress } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import axiosClient from "../../../../lib/axios";
import ReactFlow, { Background, Controls, NodeProps } from "reactflow";
import 'reactflow/dist/style.css';
import { useCallback, useEffect, useMemo } from "react";
import { enqueueSnackbar } from "notistack";
import { IDataDetalheFluxo } from "../../../../types/api/configuracoes/detalhar-fluxo";
import NodeInicio from "./FlowElements/Nodes/NodeInicio";
import NodeCondicional from "./FlowElements/Nodes/NodeCondicional";
import NodeFim from "./FlowElements/Nodes/NodeFim";
import NodeTarefa from "./FlowElements/Nodes/NodeTarefa";
import NodeAcaoExterna from "./FlowElements/Nodes/NodeAcaoExterna";
import { useDrop } from "react-dnd";
import { useFluxo } from "../../../../contexts/FlowContext";
import DialogsGallery from "./FlowElements/Dialogs";
import CustomEdge from "./FlowElements/Edges/EdgeStep";
import SideBar from "./SideBar";
import { IMutationAtualizarFluxo } from "../../../../types/mutations/bpm/atualizar-fluxo";

export default function FluxoDetalhe() {

    const {
        nodes,
        edges,
        clickedNode,
        handleNodeState,
        handleEdgeState,
        onDragItemOnBoard,
        onNodesChange,
        onEdgesChange,
        onConnect,
        onNodeClick,
        onNodeDoubleClick
    } = useFluxo();

    const { id } = useParams();

    //queries e mutations
    const { data, isLoading, refetch } = useQuery({
        queryKey: ["detalhar-fluxo", id],
        queryFn: () => axiosClient.get("/detalhar-fluxo/" + id),
        keepPreviousData: true,
        retry: false,
        cacheTime: 0,
    });
    const { dataFluxos }: IDetalheFluxo = useMemo(() => ({
        dataFluxos: data?.data
    }), [data])

    const { mutate: atualizarFluxo, isLoading: isLoadingAtualizarFluxo } = useMutation({
        mutationKey: 'atualizar-fluxo',
        mutationFn: (inputs: IMutationAtualizarFluxo) => axiosClient.put("/atualizar-fluxo/" + id, inputs),
        onSuccess: () => {
            enqueueSnackbar('Fluxo atualizado com sucesso', { variant: 'success' });
            refetch();
        },
        onError: (e) => {
            enqueueSnackbar('Erro ao atualizar fluxo', { variant: 'error' });
        },
    })
    //

    //funções manipuladoras
    const onSalvarConfiguracoes = useCallback((dadosConfiguracao: IMutationAtualizarFluxo) => {
        if (!id) {
            enqueueSnackbar('Erro ao atualizar configurações', { variant: 'error' });
            return;
        };

        atualizarFluxo(dadosConfiguracao);
    }, [atualizarFluxo, id])
    //

    const [, drop] = useDrop(() => ({
        accept: Object.keys(nodeTypes),
        collect: monitor => ({
            isOver: monitor.isOver()
        }),
        drop: onDragItemOnBoard
    }), []);
    //

    useEffect(() => {
        if (dataFluxos && dataFluxos.spec) {
            // Adiciona propriedades de visualização somente aos nós
            const nodesSomenteVisualizacao = dataFluxos.spec.nodes.map(node => ({
                ...node,
                draggable: false,
                selectable: false,
                deletable: false,
                connectable: false
            }));

            // Adiciona propriedades de visualização somente às edges
            const edgesSomenteVisualizacao = dataFluxos.spec.edges.map(edge => ({
                ...edge,
                deletable: false,
                updatable: false
            }));

            handleNodeState(nodesSomenteVisualizacao);
            handleEdgeState(edgesSomenteVisualizacao);
        }
    }, [dataFluxos])

    return (
        <>
            {(isLoading) && <CircularProgress />}

            {
                (nodes && edges && !isLoading) &&

                <Box width="100%" height="100%">
                    <Box ref={drop} width="100%" height="100%" position="relative">
                        <ReactFlow
                            fitView
                            edgeTypes={edgeTypes}
                            nodeTypes={nodeTypes}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onNodeClick={onNodeClick}
                            onNodeDoubleClick={onNodeDoubleClick}
                            edges={edges}
                            nodes={nodes.map(node => ({ ...node, data: { ...node.data, isClicked: clickedNode && clickedNode.id === node.id } }))}
                            nodesDraggable={false}
                            nodesConnectable={false}
                            elementsSelectable={false}
                        >
                            <Background />
                            <Controls position="top-right" showInteractive={false} />
                        </ReactFlow>
                    </Box>

                    <SideBar
                        isLoading={isLoadingAtualizarFluxo}
                        infoFluxo={dataFluxos}
                        onSalvarConfiguracoes={onSalvarConfiguracoes}
                    />

                    {/* <MenuOculto /> */}
                </Box>
            }

            <DialogsGallery />
        </>
    );
}

const nodeTypes = {
    "Início": NodeInicio,
    "Fim": NodeFim,
    "Condicional": NodeCondicional,
    "Condicional Automatica": NodeCondicional,
    "Tarefa": NodeTarefa,
    "Simples": NodeTarefa,
    "Ação externa": NodeAcaoExterna,
    "Verificação externa": NodeAcaoExterna,
}

const edgeTypes = {
    "step": CustomEdge,
}

interface IDetalheFluxo {
    dataFluxos: IDataDetalheFluxo
}

export interface INodeData extends NodeProps {
    data: { titulo: string, color?: string, isClicked: boolean }
}